import type { OperationVariables } from '@apollo/client'
import {
  createContext,
  useContext,
  useMemo,
  useLayoutEffect,
  useRef,
  PropsWithChildren
} from 'react'
import { useQuery } from '@apollo/client/react'
import type { ApolloUseQueryResult } from '@vivaldis/graphql'
import { useHasSession } from '@vivaldis/session'
import { userDashboardQuery } from '../../graphql/queries/userDashboardQuery'
import { UserDashboardQuery } from '../../graphql/queries/__generated__/UserDashboardQuery'

export type UserDashboardQueryContextValue = ApolloUseQueryResult<
  UserDashboardQuery,
  OperationVariables,
  'empty' | 'complete' | 'streaming',
  OperationVariables
>

export const UserDashboardQueryContext = createContext<
  UserDashboardQueryContextValue | undefined
>(undefined)

export function useUserDashboardQuery(): UserDashboardQueryContextValue {
  const context = useContext(UserDashboardQueryContext)
  if (!context) {
    throw new Error(
      'useUserDashboardQuery must be used within a UserDashboardQueryProvider'
    )
  }
  return context
}

export function useUserDashboard(): UserDashboardQuery['userDashboard'] {
  const result = useUserDashboardQuery()
  return (
    result.data?.userDashboard || {
      __typename: 'UserDashboardData',
      contractNotReadyCount: 0,
      extensionCandidatesCount: 0,
      openedSurveysCount: 0,
      unpaidInvoicesCount: 0
    }
  )
}

export const UserDashboardQueryProvider = ({
  children
}: PropsWithChildren<unknown>) => {
  const hasSession = useHasSession()
  const hadSession = useRef(hasSession)
  const options = useMemo(
    (): useQuery.Options<UserDashboardQuery> => ({
      skip: !hasSession
    }),
    [hasSession]
  )

  const result = useQuery(userDashboardQuery, options)

  const refetch = useRef(result.refetch)
  refetch.current = result.refetch
  useLayoutEffect(() => {
    if (hasSession && !hadSession.current) {
      refetch.current()
    }
    hadSession.current = hasSession
  }, [hasSession])

  return (
    <UserDashboardQueryContext.Provider value={result}>
      {children}
    </UserDashboardQueryContext.Provider>
  )
}
